import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { BagelChatMessage, BagelChatMessagePart } from '../../../types';
import { useStreamingMarkdown } from '../../hooks/useStreamingMarkdown';
import {
  getAssistantDisplayParts,
  getAssistantDisplayText,
  getBagelToolName,
  getBagelToolOutput,
  isBagelToolInProgress,
  isBagelToolPart,
} from '../../lib/assistantMessageContent';
import CopyableBlockquote from '../CopyableBlockquote';
import fenceRouter from './fenceRouter';
import BagelThinkingIndicator from './BagelThinkingIndicator';
import ToolResultCard from './ToolResultCard';
import ThinkingPill from './ThinkingPill';
import defaultStyles from './BagelChatWidget.module.scss';

dayjs.extend(relativeTime);

export type BagelMessageStyles = typeof defaultStyles;

function formatMessageTime(iso: string): string {
  const ts = dayjs(iso);
  const hoursAgo = dayjs().diff(ts, 'hour');
  if (hoursAgo < 5) {
    return ts.fromNow();
  }
  return ts.format('MMM D [at] h:mm A');
}

function TypingIndicator({ styles }: { styles: BagelMessageStyles }) {
  return (
    <div className={styles.typingIndicator} aria-label="Bagel is typing">
      <span />
      <span />
      <span />
    </div>
  );
}

function CopyButton({
  text,
  styles,
}: {
  text: string;
  styles: BagelMessageStyles;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!text.trim()) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      className={styles.copyBtn}
      onClick={handleCopy}
      title="Copy message"
      aria-label="Copy message"
    >
      {copied ? (
        <span className={styles.copiedFeedback}>Copied!</span>
      ) : (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

function createMarkdownPre(styles: BagelMessageStyles) {
  return function MarkdownPre({
    children,
  }: {
    children?: React.ReactNode;
  }) {
    const child = React.Children.toArray(
      children,
    )[0] as React.ReactElement<{
      className?: string;
      children?: React.ReactNode;
    }> | null;

    if (child?.props?.className) {
      const match = /language-(\S+)/.exec(child.props.className);
      const language = match?.[1];
      const FenceComponent = fenceRouter(language);
      if (FenceComponent) {
        const content = String(child.props.children ?? '').replace(
          /\n$/,
          '',
        );
        return <FenceComponent>{content}</FenceComponent>;
      }
    }

    return <pre className={styles.codeBlock}>{children}</pre>;
  };
}

function createMarkdownCode(styles: BagelMessageStyles) {
  return function MarkdownCode({
    className,
    children,
  }: {
    className?: string;
    children?: React.ReactNode;
  }) {
    if (!className) {
      return <code className={styles.inlineCode}>{children}</code>;
    }
    return <code className={className}>{children}</code>;
  };
}

function MarkdownLink({
  href,
  children,
  className,
  title,
}: {
  href?: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
}) {
  if (href?.startsWith('/bagel/documents/')) {
    return (
      <Link to={href} className={className} title={title}>
        {children}
      </Link>
    );
  }

  const docMatch = href?.match(/^bagel-doc:([a-f0-9]+)$/i);
  if (docMatch) {
    return (
      <Link
        to={`/bagel/documents/${docMatch[1]}`}
        className={className}
        title={title}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      title={title}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
}

function PlainBlockquote({ children }: { children?: React.ReactNode }) {
  return <blockquote>{children}</blockquote>;
}

function getUserImageAttachment(
  parts: BagelChatMessagePart[] | undefined,
): { url: string; filename?: string } | null {
  const filePart = parts?.find(
    (part): part is Extract<BagelChatMessagePart, { type: 'file' }> =>
      part.type === 'file',
  );
  return filePart?.url ? { url: filePart.url, filename: filePart.filename } : null;
}

function createMarkdownComponents(
  role: 'user' | 'assistant',
  styles: BagelMessageStyles,
) {
  return {
    pre: createMarkdownPre(styles),
    code: createMarkdownCode(styles),
    a: MarkdownLink,
    blockquote:
      role === 'assistant' ? CopyableBlockquote : PlainBlockquote,
  };
}

export interface BagelMessageProps {
  message: BagelChatMessage;
  isStreaming: boolean;
  stylesModule?: BagelMessageStyles;
}

function MarkdownBody({
  text,
  isStreaming,
  messageRole,
  styles,
}: {
  text: string;
  isStreaming: boolean;
  messageRole: 'user' | 'assistant';
  styles: BagelMessageStyles;
}) {
  const { safeMarkdown, pendingTail } = useStreamingMarkdown(
    text,
    isStreaming,
  );
  const markdownComponents = useMemo(
    () => createMarkdownComponents(messageRole, styles),
    [messageRole, styles],
  );

  if (!safeMarkdown && !pendingTail) return null;

  return (
    <div className={`${styles.bubble} ${styles.markdownBody}`}>
      {safeMarkdown && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {safeMarkdown}
        </ReactMarkdown>
      )}
      {pendingTail && (
        <span className={styles.streamingTail}>{pendingTail}</span>
      )}
    </div>
  );
}

export default function BagelMessage({
  message,
  isStreaming,
  stylesModule = defaultStyles,
}: BagelMessageProps) {
  const styles = stylesModule;
  const { role, content, parts, thinkingTool, thinkingText, timestamp } =
    message;
  const suppressInterimText = Boolean(thinkingTool);
  const displayParts = useMemo(
    () =>
      getAssistantDisplayParts(parts, {
        isStreaming,
        suppressInterimText,
      }),
    [parts, isStreaming, suppressInterimText],
  );
  const displayText = useMemo(
    () =>
      getAssistantDisplayText(
        { role, content, parts },
        { isStreaming, suppressInterimText },
      ),
    [role, content, parts, isStreaming, suppressInterimText],
  );
  const markdownComponents = useMemo(
    () => createMarkdownComponents(role, styles),
    [role, styles],
  );

  if (role === 'user') {
    const attachment = getUserImageAttachment(parts);
    return (
      <div className={`${styles.message} ${styles.user}`}>
        {attachment && (
          <img
            src={attachment.url}
            alt={attachment.filename ?? 'Uploaded image'}
            className={styles.userAttachmentImage}
          />
        )}
        {content && <div className={styles.bubble}>{content}</div>}
        {(timestamp || content) && (
          <div
            className={`${styles.messageMeta} ${styles.messageMetaUser}`}
          >
            {content && <CopyButton text={content} styles={styles} />}
            {timestamp && (
              <span className={styles.messageTimestamp}>
                {formatMessageTime(timestamp)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  const hasDisplayParts = Boolean(displayParts?.length);
  const showTypingIndicator =
    isStreaming && !displayText && !thinkingTool && !hasDisplayParts;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { safeMarkdown, pendingTail } = useStreamingMarkdown(
    hasDisplayParts ? '' : displayText,
    isStreaming && !hasDisplayParts,
  );
  const legacyFullContent = safeMarkdown + (pendingTail || '');

  return (
    <div className={`${styles.message} ${styles.assistant}`}>
      {thinkingTool && (
        <BagelThinkingIndicator
          text={thinkingText}
          tool={thinkingTool}
          isActive={isStreaming}
        />
      )}

      {showTypingIndicator && <TypingIndicator styles={styles} />}

      {hasDisplayParts
        ? displayParts!.map((part, i) => {
            if (part.type === 'text' && part.text) {
              return (
                <MarkdownBody
                  key={`text-${i}`}
                  text={part.text}
                  isStreaming={isStreaming}
                  messageRole="assistant"
                  styles={styles}
                />
              );
            }

            if (isBagelToolPart(part)) {
              const toolName = getBagelToolName(part) ?? 'tool';

              if (isBagelToolInProgress(part)) {
                return (
                  <ThinkingPill
                    key={`tool-${i}`}
                    label={`Calling ${toolName}…`}
                    tool={toolName}
                  />
                );
              }

              const output = getBagelToolOutput(part);
              if (output) {
                return (
                  <ToolResultCard
                    key={`tool-${i}`}
                    toolName={toolName}
                    result={
                      output as {
                        found?: boolean;
                        results?: {
                          text: string;
                          title: string;
                          year?: string;
                          sourceId: string;
                          documentUrl?: string;
                          fileType?: string;
                        }[];
                        message?: string;
                      }
                    }
                  />
                );
              }
            }

            return null;
          })
        : (safeMarkdown || pendingTail) && (
            <div className={`${styles.bubble} ${styles.markdownBody}`}>
              {safeMarkdown && (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {safeMarkdown}
                </ReactMarkdown>
              )}
              {pendingTail && (
                <span className={styles.streamingTail}>{pendingTail}</span>
              )}
            </div>
          )}

      {!isStreaming && (timestamp || legacyFullContent || displayText) && (
        <div
          className={`${styles.messageMeta} ${styles.messageMetaAssistant}`}
        >
          {(legacyFullContent.trim() || displayText.trim()) && (
            <CopyButton
              text={legacyFullContent || displayText}
              styles={styles}
            />
          )}
          {timestamp && (
            <span className={styles.messageTimestamp}>
              {formatMessageTime(timestamp)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
